import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { BackBar } from "../components/BackBar";
import { ShieldCheck, Globe, FileText, GraduationCap, Newspaper } from "lucide-react";

const CONTACT_EMAIL = "info@ofalycee.fr";
const BLOG_CONTACT_EMAIL = "ovincent@ofalycee.org";

const FR = {
  title: "Politique de confidentialité",
  updated: "Dernière mise à jour : février 2026",
  badge: "Conforme RGPD & FERPA · Politique unique multi-plateformes",
  intro:
    "OFA France s'engage à la plus stricte protection des données personnelles de ses élèves, parents, partenaires et visiteurs. Cette politique couvre l'ensemble de nos plateformes en ligne.",
  controllerTitle: "Responsable du traitement",
  controllerBody:
    "OFALycée, Inc. (« OFA France »)\n27 Berkshire Drive, Winchester MA 01890, États-Unis\nEIN 85-3923807\nContact : info@ofalycee.fr",
  platformsTitle: "Nos plateformes et les données traitées",
  platforms: [
    {
      icon: "globe",
      domain: "www.ofalycee.fr",
      label: "Site institutionnel",
      lines: [
        "Données collectées : formulaire de contact (prénom, nom, email, message).",
        "Stockage : aucun sur nos serveurs — le message est transmis par email via Resend.",
        "Cookies : Google Analytics (uniquement avec votre consentement).",
      ],
    },
    {
      icon: "file",
      domain: "admissions.ofalycee.fr",
      label: "Portail d'admission",
      lines: [
        "Cette plateforme ne stocke aucune donnée personnelle sur ses propres serveurs. Les données saisies dans les formulaires sont collectées par OFA France via Microsoft Forms (Microsoft 365).",
        "Les formulaires de demande d'informations et d'inscription sont hébergés par Microsoft Forms (Microsoft 365, zone Union européenne, conforme RGPD).",
        "Les paiements éventuels sont effectués directement par Stripe, Inc. (certifié PCI-DSS Level 1). Aucune donnée bancaire ne transite par nos serveurs.",
      ],
    },
    {
      icon: "academy",
      domain: "academy.ofalycee.org",
      label: "Plateforme académique",
      lines: [
        "Données collectées : compte parent (prénom, nom, email, mot de passe haché).",
        "Paiements : effectués via Stripe, Inc. (PCI-DSS Level 1).",
        "Les contenus pédagogiques (progression, résultats, certificats) sont gérés et délivrés exclusivement par Canvas LMS (Instructure, Inc.) — voir leur propre politique de confidentialité.",
        "Conservation : durée de la relation contractuelle + obligations légales comptables.",
      ],
    },
    {
      icon: "news",
      domain: "blog.ofalycee.org",
      label: "Blog éditorial",
      lines: [
        "Pas de compte utilisateur — uniquement consultation libre.",
        "Données collectées : formulaire de contact (nom, email, sujet, message).",
        `Stockage : aucun — le message est transmis par email à ${BLOG_CONTACT_EMAIL}.`,
      ],
    },
  ],
  sections: [
    {
      h: "Bases légales du traitement",
      b: "• Consentement (cookies analytiques, communications marketing).\n• Exécution d'un contrat (admission, inscription, scolarité, paiement).\n• Intérêt légitime (amélioration des services, prévention de la fraude, sécurité).\n• Obligation légale (comptabilité, conservation des documents pédagogiques).",
    },
    {
      h: "Vos droits (RGPD)",
      b: `Conformément au RGPD (UE 2016/679) et à la loi française Informatique et Libertés, vous disposez des droits suivants : accès, rectification, effacement, opposition, portabilité, limitation du traitement et retrait du consentement à tout moment.\n\nPour exercer ces droits : ${CONTACT_EMAIL}\nNous répondons sous 30 jours maximum.\n\nEn cas de désaccord, vous pouvez introduire une réclamation auprès de la CNIL (www.cnil.fr).`,
    },
    {
      h: "Droits FERPA (USA)",
      b: `OFALycée, Inc. est une école accréditée aux États-Unis et se conforme au Family Educational Rights and Privacy Act (FERPA, 20 U.S.C. § 1232g).\n\nLes parents d'élèves mineurs (et les élèves âgés de 18 ans ou plus) disposent des droits suivants sur les dossiers scolaires (« education records ») :\n\n• Droit d'accès et d'inspection des dossiers scolaires sous 45 jours après demande écrite.\n• Droit de demander la rectification de toute information jugée inexacte, trompeuse ou portant atteinte à la vie privée.\n• Droit de contrôle de la divulgation : aucune information personnelle identifiable (PII) du dossier scolaire n'est divulguée à un tiers sans consentement écrit préalable, sauf exceptions prévues par FERPA (école officielle, transferts inter-établissements, obligations légales, situations d'urgence).\n• Droit de déposer une plainte auprès du Family Policy Compliance Office (U.S. Department of Education) en cas de non-respect.\n\nLes données pédagogiques (notes, devoirs, progression, certificats) sont gérées et hébergées principalement dans Canvas LMS (Instructure, Inc.), qui agit comme prestataire de services (« school official ») conformément à FERPA, dans le cadre des services fournis à OFALycée.\n\nInformations dites « directory information » : OFA France ne publie aucune information annuaire (nom, photo, dates) sans consentement écrit préalable du parent.\n\nPour exercer vos droits FERPA : ${CONTACT_EMAIL}\nFamily Policy Compliance Office : 400 Maryland Avenue SW, Washington, DC 20202-8520, USA.`,
    },
    {
      h: "Sous-traitants et transferts de données",
      b: "• Microsoft Corporation (Forms, Outlook) — données stockées en UE.\n• Stripe, Inc. (paiements) — UE / USA, certifié Data Privacy Framework (DPF).\n• Resend (envoi d'emails transactionnels) — UE / USA, certifié DPF.\n• Google LLC (Google Analytics 4) — UE / USA, certifié DPF.\n• Instructure, Inc. (Canvas LMS) — USA, certifié DPF.\n\nTransferts internationaux : lorsque des données sont transférées en dehors de l'Espace économique européen, OFA France veille à ce que ces transferts soient encadrés conformément au RGPD, notamment par le Data Privacy Framework ou, lorsque nécessaire, par les Clauses Contractuelles Types de la Commission européenne.\n\nNous ne vendons, ne louons ni ne commercialisons vos données personnelles.",
    },
    {
      h: "Sécurité",
      b: "Nous mettons en œuvre les mesures techniques et organisationnelles suivantes :\n• Chiffrement des communications (HTTPS / TLS) sur toutes nos plateformes.\n• Mots de passe stockés sous forme hachée et salée (bcrypt).\n• Accès aux données restreint au personnel habilité (principe du moindre privilège).\n• Limitation du taux de requêtes (rate limiting), protection XSS et CSRF.\n• Chiffrement au repos chez nos sous-traitants certifiés.",
    },
    {
      h: "Cookies",
      b: "• Site institutionnel et blog : cookies techniques (préférences de langue) + cookies analytiques Google Analytics (uniquement avec votre consentement, modifiable à tout moment via le bandeau).\n• Plateformes Microsoft Forms et Academy : cookies de session strictement nécessaires au fonctionnement.\n• Stripe : cookies nécessaires à la sécurité du paiement.\nDurée maximale des cookies : 13 mois.",
    },
    {
      h: "Modifications",
      b: "Nous nous réservons le droit de modifier cette politique. Toute modification sera publiée sur cette page avec la date de mise à jour. La version en vigueur fait foi.",
    },
  ],
};

const EN = {
  title: "Privacy Policy",
  updated: "Last update: February 2026",
  badge: "GDPR & FERPA compliant · Single multi-platform policy",
  intro:
    "OFA France is committed to the highest standards of personal data protection for its students, parents, partners and visitors. This policy covers all our online platforms.",
  controllerTitle: "Data Controller",
  controllerBody:
    "OFALycée, Inc. (\"OFA France\")\n27 Berkshire Drive, Winchester MA 01890, USA\nEIN 85-3923807\nContact: info@ofalycee.fr",
  platformsTitle: "Our platforms and the data we process",
  platforms: [
    {
      icon: "globe",
      domain: "www.ofalycee.fr",
      label: "Institutional website",
      lines: [
        "Data collected: contact form (first name, last name, email, message).",
        "Storage: none on our servers — messages are forwarded by email via Resend.",
        "Cookies: Google Analytics (with your consent only).",
      ],
    },
    {
      icon: "file",
      domain: "admissions.ofalycee.fr",
      label: "Admissions portal",
      lines: [
        "This platform does not store any personal data on its own servers. Data entered in forms is collected by OFA France through Microsoft Forms (Microsoft 365).",
        "Information and enrollment forms are hosted by Microsoft Forms (Microsoft 365, European Union region, GDPR-compliant).",
        "Any payments are processed directly by Stripe, Inc. (PCI-DSS Level 1). No bank data transits through our servers.",
      ],
    },
    {
      icon: "academy",
      domain: "academy.ofalycee.org",
      label: "Academic platform",
      lines: [
        "Data collected: parent account (first name, last name, email, hashed password).",
        "Payments: processed via Stripe, Inc. (PCI-DSS Level 1).",
        "Educational content (progress, results, certificates) is managed and issued exclusively by Canvas LMS (Instructure, Inc.) — see their own privacy policy.",
        "Retention: duration of the contractual relationship + accounting legal obligations.",
      ],
    },
    {
      icon: "news",
      domain: "blog.ofalycee.org",
      label: "Editorial blog",
      lines: [
        "No user account — read-only access.",
        "Data collected: contact form (name, email, subject, message).",
        `Storage: none — messages are forwarded by email to ${BLOG_CONTACT_EMAIL}.`,
      ],
    },
  ],
  sections: [
    {
      h: "Legal bases for processing",
      b: "• Consent (analytics cookies, marketing communications).\n• Performance of a contract (admission, enrollment, schooling, payment).\n• Legitimate interest (service improvement, fraud prevention, security).\n• Legal obligation (accounting, retention of academic records).",
    },
    {
      h: "Your rights (GDPR)",
      b: `Under the GDPR (EU 2016/679) and the French Data Protection Act, you have the right to access, rectify, erase, object, port, restrict processing and withdraw your consent at any time.\n\nTo exercise these rights: ${CONTACT_EMAIL}\nWe respond within 30 days maximum.\n\nIn case of disagreement, you may file a complaint with the French CNIL (www.cnil.fr).`,
    },
    {
      h: "FERPA Rights (USA)",
      b: `OFALycée, Inc. is a U.S. accredited school and complies with the Family Educational Rights and Privacy Act (FERPA, 20 U.S.C. § 1232g).\n\nParents of minor students (and students aged 18 or older) have the following rights regarding education records:\n\n• Right to inspect and review education records within 45 days of a written request.\n• Right to request amendment of any record believed to be inaccurate, misleading or otherwise in violation of privacy rights.\n• Right to control disclosure: no personally identifiable information (PII) from education records is disclosed to third parties without prior written consent, except as permitted by FERPA (school officials, school transfers, legal obligations, health/safety emergencies).\n• Right to file a complaint with the Family Policy Compliance Office (U.S. Department of Education).\n\nEducational data (grades, assignments, progress, certificates) is primarily managed and hosted in Canvas LMS (Instructure, Inc.), which acts as a service provider ("school official") in accordance with FERPA, within the framework of services provided to OFALycée.\n\n"Directory information": OFA France does not publish any directory information (name, photo, dates) without prior written parental consent.\n\nTo exercise your FERPA rights: ${CONTACT_EMAIL}\nFamily Policy Compliance Office: 400 Maryland Avenue SW, Washington, DC 20202-8520, USA.`,
    },
    {
      h: "Subprocessors and data transfers",
      b: "• Microsoft Corporation (Forms, Outlook) — data stored in the EU.\n• Stripe, Inc. (payments) — EU / USA, Data Privacy Framework (DPF) certified.\n• Resend (transactional emails) — EU / USA, DPF certified.\n• Google LLC (Google Analytics 4) — EU / USA, DPF certified.\n• Instructure, Inc. (Canvas LMS) — USA, DPF certified.\n\nInternational transfers: when data is transferred outside the European Economic Area, OFA France ensures that such transfers are framed in accordance with the GDPR, in particular through the Data Privacy Framework or, where necessary, the European Commission's Standard Contractual Clauses.\n\nWe do not sell, rent or commercialize your personal data.",
    },
    {
      h: "Security",
      b: "We implement the following technical and organizational measures:\n• Communications encryption (HTTPS / TLS) on all platforms.\n• Passwords stored hashed and salted (bcrypt).\n• Access restricted to authorized staff (least privilege principle).\n• Rate limiting, XSS and CSRF protection.\n• Encryption at rest with our certified subprocessors.",
    },
    {
      h: "Cookies",
      b: "• Institutional site and blog: technical cookies (language preferences) + Google Analytics cookies (consent required, adjustable via banner).\n• Microsoft Forms and Academy: strictly necessary session cookies.\n• Stripe: cookies required for payment security.\nMaximum cookie lifespan: 13 months.",
    },
    {
      h: "Changes to this policy",
      b: "We reserve the right to update this policy. Any change will be published on this page with the update date. The version in force prevails.",
    },
  ],
};

function PlatformIcon({ name, className }) {
  const map = {
    globe: <Globe className={className} aria-hidden="true" />,
    file: <FileText className={className} aria-hidden="true" />,
    academy: <GraduationCap className={className} aria-hidden="true" />,
    news: <Newspaper className={className} aria-hidden="true" />,
  };
  return map[name] || <Globe className={className} aria-hidden="true" />;
}

export default function PrivacyPage() {
  const { lang } = useLang();
  const c = lang === "fr" ? FR : EN;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10" data-testid="privacy-page">
      <BackBar to="/" fallbackLabelFr="Accueil" fallbackLabelEn="Home" />
      <p className="overline mb-3">Legal · {c.updated}</p>
      <h1
        className="font-display font-bold text-4xl lg:text-5xl tracking-tighter mb-4"
        data-testid="privacy-title"
      >
        {c.title}
      </h1>

      <div
        className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 mb-6"
        data-testid="privacy-badge"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-accent" aria-hidden="true" />
        <span className="text-xs font-medium text-accent">{c.badge}</span>
      </div>

      <p className="text-muted-foreground leading-relaxed mb-10" data-testid="privacy-intro">
        {c.intro}
      </p>

      <section className="mb-10" data-testid="privacy-controller">
        <h2 className="font-display font-semibold text-xl mb-2">{c.controllerTitle}</h2>
        <p className="text-muted-foreground leading-relaxed text-sm whitespace-pre-line">{c.controllerBody}</p>
      </section>

      <section className="mb-10" data-testid="privacy-platforms">
        <h2 className="font-display font-semibold text-xl mb-4">{c.platformsTitle}</h2>
        <div className="space-y-4">
          {c.platforms.map((p) => (
            <div
              key={p.domain}
              className="rounded-xl border border-border bg-card/40 p-5"
              data-testid={`privacy-platform-${p.domain}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <PlatformIcon name={p.icon} className="w-5 h-5 text-accent" />
                <div>
                  <div className="font-mono text-sm font-semibold">{p.domain}</div>
                  <div className="text-xs text-muted-foreground">{p.label}</div>
                </div>
              </div>
              <ul className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                {p.lines.map((l, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-accent flex-shrink-0">•</span>
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <div className="space-y-8">
        {c.sections.map((s) => (
          <section key={s.h}>
            <h2 className="font-display font-semibold text-xl mb-2">{s.h}</h2>
            <p className="text-muted-foreground leading-relaxed text-sm whitespace-pre-line">{s.b}</p>
          </section>
        ))}
      </div>

      <div className="mt-12 pt-6 border-t border-border text-sm text-muted-foreground">
        <Link to="/" className="text-accent hover:underline" data-testid="privacy-back-home">
          ← {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>
      </div>
    </div>
  );
}
